/**
 * FASE 22.1 — WORKFLOW STATE STORE
 *
 * In-memory workflow list. Max 15 active workflows.
 * Persists to localStorage on every mutation (7-day TTL).
 *
 * No React deps. Singleton module state.
 */

import { WORKFLOW_STATUS, computeProgress } from "./workflowSchemas";
import { recoverWorkflows }                 from "../kevia-runtime/runtimeRecovery";

const STORAGE_KEY    = "kv_workflows_v1";
const SCHEMA_VERSION = "wf_v1";
const TTL_MS         = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_WORKFLOWS  = 15;
const MAX_SIZE_BYTES = 10_240; // 10 KB

/** @type {Workflow[]} */
let _workflows   = [];
let _loaded      = false;
let _persistTimer = null;

/* ─── Persistence ─────────────────────────────────────── */

function _persistNow() {
  try {
    const payload = {
      _schema:    SCHEMA_VERSION,
      _timestamp: Date.now(),
      workflows:  _workflows,
    };
    const json = JSON.stringify(payload);
    if (json.length > MAX_SIZE_BYTES) {
      // Evict archived/completed workflows first
      _workflows = _workflows
        .filter((w) => w.status === WORKFLOW_STATUS.ACTIVE || w.status === WORKFLOW_STATUS.PAUSED)
        .slice(0, MAX_WORKFLOWS);
      const trimmed = JSON.stringify({ ...payload, workflows: _workflows });
      localStorage.setItem(STORAGE_KEY, trimmed);
      return;
    }
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    // localStorage unavailable — silent
  }
}

/** Debounced persist — coalesces rapid mutations into a single write (100 ms). */
function _persist() {
  if (_persistTimer !== null) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    _persistNow();
  }, 100);
}

function _load() {
  if (_loaded) return;
  _loaded = true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?._schema !== SCHEMA_VERSION) return;
    if (Date.now() - (parsed._timestamp ?? 0) > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    _workflows = recoverWorkflows(Array.isArray(parsed.workflows) ? parsed.workflows : []);
  } catch {
    _workflows = [];
  }
}

/* ─── Public store API ──────────────────────────────────── */

export function getWorkflows() {
  _load();
  return [..._workflows];
}

export function getActiveWorkflows() {
  _load();
  return _workflows.filter(
    (w) => w.status === WORKFLOW_STATUS.ACTIVE || w.status === WORKFLOW_STATUS.PAUSED,
  );
}

export function getWorkflowById(id) {
  _load();
  return _workflows.find((w) => w.id === id) ?? null;
}

export function addWorkflow(workflow) {
  _load();
  if (_workflows.length >= MAX_WORKFLOWS) {
    // Evict oldest archived workflow first
    const oldestArchived = _workflows
      .filter((w) => w.status === WORKFLOW_STATUS.ARCHIVED || w.status === WORKFLOW_STATUS.COMPLETED)
      .sort((a, b) => a.updated_at - b.updated_at)[0];
    if (oldestArchived) {
      _workflows = _workflows.filter((w) => w.id !== oldestArchived.id);
    } else {
      return false; // At capacity, nothing to evict
    }
  }
  _workflows = [..._workflows, workflow];
  _persist();
  return true;
}

export function updateWorkflow(id, changes) {
  _load();
  let found = false;
  _workflows = _workflows.map((w) => {
    if (w.id !== id) return w;
    found = true;
    const updated = {
      ...w,
      ...changes,
      updated_at:    Date.now(),
      last_activity: Date.now(),
    };
    // Recompute progress if steps were updated
    if (changes.steps) updated.progress = computeProgress(changes.steps);
    return updated;
  });
  if (found) _persist();
  return found;
}

export function removeWorkflow(id) {
  _load();
  const prev = _workflows.length;
  _workflows = _workflows.filter((w) => w.id !== id);
  if (_workflows.length !== prev) _persist();
}

/** Used by restore from snapshot. */
export function setWorkflows(workflows) {
  _workflows = (Array.isArray(workflows) ? workflows : []).slice(0, MAX_WORKFLOWS);
  _loaded    = true;
  _persist();
}

export function clearWorkflows() {
  if (_persistTimer !== null) { clearTimeout(_persistTimer); _persistTimer = null; }
  _workflows = [];
  _loaded    = true;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}
